import { type MouseEvent } from 'react';
import { ArrowUpRight, Plus } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { NavLink, useLocation } from 'react-router';

import { Button } from '@accio3d/design-system/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@accio3d/design-system/tooltip';
import { cn } from '@accio3d/design-system/utils';
import arcessioIconSrc from '@accio3d/shared-ui/assets/arcessio-icon.svg';
import arcessioLogoSrc from '@accio3d/shared-ui/assets/arcessio-logo.svg';

import { OrgAudience } from '../../guards/permissions';
import { navGroupsForOrgType, SETTINGS_ITEM, type NavItem } from '../../config/nav';
import { useAuth } from '../../hooks/useAuth';
import { useNavCounts } from '../../hooks/useNavCounts';
import { OrganizationSwitcher } from './organization-switcher';
import { NotificationsBell } from './notifications-bell';

const SIDEBAR_BORDER = '1px solid hsl(var(--sidebar-border))';
const SIDEBAR_SHADOW = 'none';

interface AppSidebarProps {
  collapsed: boolean;
  onExpand: () => void;
  onOpenOrgModal?: () => void;
}

export function AppSidebar({ collapsed, onExpand, onOpenOrgModal }: AppSidebarProps) {
  const location = useLocation();
  const { organization } = useAuth();
  const { data: counts } = useNavCounts();

  const navGroups = navGroupsForOrgType(organization?.type);
  const isPurchaser = (organization?.type ?? '').toLowerCase() === OrgAudience.PURCHASER;

  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + '/');

  const handleRailClick = (event: MouseEvent<HTMLDivElement>) => {
    if (!collapsed) return;
    const hitControl = (event.target as HTMLElement).closest('a,button');
    if (hitControl) return;
    onExpand();
  };

  const renderNavItem = (item: NavItem) => {
    const active = isActive(item.path);
    const count = item.badgeKey ? (counts?.[item.badgeKey] ?? 0) : 0;
    const badge = count > 0 ? (count > 99 ? '99+' : String(count)) : null;
    const label = badge ? `${item.label} (${count} need attention)` : item.label;

    const link = (
      <NavLink
        to={item.path}
        aria-label={label}
        className={cn(
          'flex items-center rounded-sm border text-xs transition-colors',
          collapsed
            ? 'size-[34px] shrink-0 justify-center'
            : 'min-h-[39px] w-full justify-start gap-3 px-3 py-2.5 text-left',
          active
            ? 'border-sidebar-active-border bg-sidebar-active text-white'
            : 'border-transparent text-sidebar-foreground hover:bg-sidebar-accent hover:text-white',
        )}
      >
        <span className="relative flex shrink-0 items-center">
          <item.icon
            size={17}
            strokeWidth={1.5}
            className={cn('shrink-0', active && 'text-sidebar-primary')}
          />
          {badge && collapsed && (
            <span
              aria-hidden
              className="absolute -right-2 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-white px-1 text-[10px] font-bold leading-none text-slate-900"
            >
              {badge}
            </span>
          )}
        </span>
        <AnimatePresence>
          {!collapsed && (
            <motion.span
              initial={{ opacity: 0, width: 0 }}
              animate={{ opacity: 1, width: 'auto' }}
              exit={{ opacity: 0, width: 0 }}
              transition={{ duration: 0.15 }}
              className="overflow-hidden whitespace-nowrap text-xs font-medium"
            >
              {item.label}
            </motion.span>
          )}
        </AnimatePresence>
        {badge && !collapsed && (
          <span
            aria-hidden
            className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-white px-1.5 text-[11px] font-bold leading-none text-slate-900"
          >
            {badge}
          </span>
        )}
      </NavLink>
    );

    if (!collapsed) {
      return (
        <div key={item.path} className="w-full">
          {link}
        </div>
      );
    }

    return (
      <Tooltip key={item.path}>
        <TooltipTrigger asChild>{link}</TooltipTrigger>
        <TooltipContent side="right" className="rounded-xl">
          {badge ? `${item.label} (${count})` : item.label}
        </TooltipContent>
      </Tooltip>
    );
  };

  return (
    <TooltipProvider delayDuration={200}>
      <motion.aside
        animate={{ width: collapsed ? 64 : 240 }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        className="flex shrink-0 flex-col items-stretch overflow-hidden px-2"
        style={{
          margin: 0,
          borderRadius: 0,
          background: 'var(--sidebar-gradient)',
          border: SIDEBAR_BORDER,
          borderRight: 'none',
          boxShadow: SIDEBAR_SHADOW,
        }}
      >
        <div
          onClick={handleRailClick}
          className={cn(
            'flex h-full w-full flex-col py-3',
            collapsed ? 'items-center cursor-e-resize' : 'items-stretch',
          )}
        >
          <div className="mb-4 flex min-h-[100px] w-full flex-col items-center justify-center gap-1 border-b border-sidebar-border pb-4">
            <AnimatePresence mode="wait">
              {collapsed ? (
                <motion.div
                  key="icon"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  transition={{ duration: 0.15 }}
                  className="flex h-12 w-12 items-center justify-center"
                >
                  <img
                    src={arcessioIconSrc}
                    alt="Arcessio"
                    className="h-8 w-8 object-contain brightness-0 invert"
                  />
                </motion.div>
              ) : (
                <motion.div
                  key="logo"
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -8 }}
                  transition={{ duration: 0.15 }}
                  className="flex w-full items-center justify-center px-4"
                >
                  <img
                    src={arcessioLogoSrc}
                    alt="Arcessio"
                    className="h-12 object-contain brightness-0 invert"
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <div className={cn('w-full pb-3', collapsed ? 'flex justify-center' : 'px-2')}>
            {isPurchaser &&
              (collapsed ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      asChild
                      variant="gradient"
                      size="icon"
                      aria-label="New RFQ"
                      className="size-[34px]"
                    >
                      <NavLink to="/rfqs/new-rfq">
                        <Plus className="h-4 w-4" />
                      </NavLink>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="right">New RFQ</TooltipContent>
                </Tooltip>
              ) : (
                <Button asChild variant="gradient" className="w-full justify-between">
                  <NavLink to="/rfqs/new-rfq">
                    <span className="flex items-center gap-2">
                      <Plus className="h-4 w-4" />
                      New RFQ
                    </span>
                    <ArrowUpRight className="h-3.5 w-3.5" />
                  </NavLink>
                </Button>
              ))}
          </div>

          <nav
            className={cn(
              'flex w-full flex-1 flex-col gap-3',
              collapsed ? 'items-center' : 'items-stretch px-2',
            )}
          >
            {navGroups.map((group) => (
              <div
                key={group.label}
                className={cn('flex w-full flex-col gap-2', collapsed && 'items-center')}
              >
                {!collapsed && <span className="label-caps-sidebar px-3 pb-2">{group.label}</span>}
                {group.items.map(renderNavItem)}
              </div>
            ))}
          </nav>

          <div
            className={cn(
              'flex w-full flex-col gap-1.5 pb-2',
              collapsed ? 'items-center' : 'items-stretch px-2',
            )}
          >
            <NotificationsBell collapsed={collapsed} />
            {renderNavItem(SETTINGS_ITEM)}
          </div>

          <div
            className={cn(
              'w-full px-2 pb-1',
              collapsed ? 'flex flex-col items-center gap-1' : 'space-y-1',
            )}
          >
            <OrganizationSwitcher collapsed={collapsed} onOpenModal={onOpenOrgModal} />
          </div>
        </div>
      </motion.aside>
    </TooltipProvider>
  );
}
